import { Experiment, ExperimentClient } from "@amplitude/experiment-js-client";
import { Variant, VariantEnum } from "./types";
import { DEPLOYMENT_KEY } from "./deploymentKey";

// Module-level singleton shared across all hook instances for the page lifetime.
let experimentClientInstance: ExperimentClient | undefined;

let inFlight: Map<string, Promise<void>> = new Map();

let lastFetchedIdentity: string | undefined;

const getClient = (): ExperimentClient => {
  if (!experimentClientInstance) {
    experimentClientInstance = Experiment.initializeWithAmplitudeAnalytics(
      DEPLOYMENT_KEY,
      { automaticFetchOnAmplitudeIdentityChange: false }
    );
  }
  return experimentClientInstance;
};

export const resetExperimentClientForTests = (): void => {
  experimentClientInstance = undefined;
  inFlight = new Map();
  lastFetchedIdentity = undefined;
};

export const fetchVariantForUser = async (
  accountNumber: string,
  deviceId: string | undefined,
  experimentKey: string
): Promise<Variant<VariantEnum, unknown> | undefined> => {
  const client = getClient();
  const identityKey = accountNumber;

  if (
    lastFetchedIdentity !== undefined &&
    lastFetchedIdentity !== identityKey
  ) {
    lastFetchedIdentity = undefined;
  }

  if (lastFetchedIdentity === identityKey) {
    return client.variant(experimentKey, undefined) as
      | Variant<VariantEnum, unknown>
      | undefined;
  }

  if (!inFlight.has(identityKey)) {
    const fetchPromise = client
      .fetch({ user_id: accountNumber, device_id: deviceId })
      .then(() => {
        lastFetchedIdentity = identityKey;
      })
      .finally(() => {
        inFlight.delete(identityKey);
      });
    inFlight.set(identityKey, fetchPromise);
  }

  await inFlight.get(identityKey);

  return client.variant(experimentKey, undefined) as
    | Variant<VariantEnum, unknown>
    | undefined;
};
